import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import type { VideoOperationType } from '../../../domain';
import type { VideoPanelProps } from './panelTypes';
import { TextToVideoPanel } from './TextToVideoPanel';
import { ImageToVideoPanel } from './ImageToVideoPanel';
import { InterpolatePanel } from './InterpolatePanel';
import { ExtendPanel } from './ExtendPanel';
import { RestylePanel } from './RestylePanel';
import { HeavyCreationOverlay } from '../../../shell/HeavyCreationOverlay';
import { useMobileShell } from '../../../shell/useMobileShell';

interface Props extends VideoPanelProps {
  kind: VideoOperationType;
}

const PANELS: Record<VideoOperationType, React.FC<VideoPanelProps>> = {
  TextToVideo: TextToVideoPanel,
  ImageToVideo: ImageToVideoPanel,
  Interpolate: InterpolatePanel,
  Extend: ExtendPanel,
  Restyle: RestylePanel,
};

/**
 * Every video op is heavy (PL-5): each kicks off a long, paid upstream render
 * driven by inputs (prompt tuning, source/frame uploads, video selection) that
 * are awkward on a phone. On the mobile shell we defer the whole set behind the
 * HeavyCreationOverlay — mirrors Image Lab's `HEAVY_OPS` gate.
 */
const VIDEO_HEAVY_OPS = new Set<VideoOperationType>([
  'TextToVideo',
  'ImageToVideo',
  'Interpolate',
  'Extend',
  'Restyle',
]);

const VIDEO_OP_LABELS: Record<VideoOperationType, string> = {
  TextToVideo: 'Text to Video',
  ImageToVideo: 'Animate Still',
  Interpolate: 'Interpolate Frames',
  Extend: 'Extend Clip',
  Restyle: 'Restyle Clip',
};

/** Dispatches the active op to its panel (mirrors Image/Writer Lab dispatchers). */
export const AnimationOperationPanel: React.FC<Props> = ({ kind, ...panelProps }) => {
  const Panel = PANELS[kind];
  const { isMobileShell } = useMobileShell();

  // Per-op override: re-affirmed on each op switch so "Continue anyway" doesn't
  // silently carry over to the next heavy op (same contract as Image Lab).
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!isMobileShell) setOverrides({});
  }, [isMobileShell]);

  const isHeavyMobile = isMobileShell && VIDEO_HEAVY_OPS.has(kind) && !overrides[kind];

  return (
    <Box sx={{ position: 'relative', minHeight: isHeavyMobile ? 360 : undefined }}>
      <Panel {...panelProps} />
      {isHeavyMobile && (
        <HeavyCreationOverlay
          operationLabel={VIDEO_OP_LABELS[kind] ?? kind}
          onContinueAnyway={() => setOverrides(prev => ({ ...prev, [kind]: true }))}
          onViewReadOnly={() => setOverrides(prev => ({ ...prev, [kind]: true }))}
        />
      )}
    </Box>
  );
};
